import { useEffect, useRef, useState } from "react";
import { Upload, FileText, Loader2, Link2, Image as ImageIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { readFileAsDataURL, downsizeImage, classifyUpload } from "@/lib/utils";
import { uploadImage, analyzeImage, importFromUrl } from "@/api";
import DocumentImportDialog from "@/components/DocumentImportDialog";

const isPdf = (f) =>
  f?.type === "application/pdf" || (f?.name || "").toLowerCase().endsWith(".pdf");

/**
 * Wraps children with a full-area drag-and-drop zone for images and PDFs.
 * - Files are uploaded to /api/projects/{id}/images (base64 in Mongo)
 * - URL drops (text/uri-list or text/plain) are sent to /api/import/url
 *
 * Props:
 *   projectId
 *   category        default category for image uploads (PDFs ignore this)
 *   onUploaded(img) called after every successful upload
 *   className       wrapper class
 *   children        wrapped UI
 */
export default function DropZone({
  projectId,
  category = "inspiration",
  onUploaded,
  className = "",
  children,
}) {
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [pdfQueue, setPdfQueue] = useState([]);
  const [docOpen, setDocOpen] = useState(false);
  const counter = useRef(0); // robust enter/leave counter

  async function uploadFiles(files) {
    if (!files?.length) return;
    // PDFs go through the conversion dialog; everything else uploads directly
    const pdfs = Array.from(files).filter(isPdf);
    const others = Array.from(files).filter((f) => !isPdf(f));
    if (others.length) await uploadDirect(others);
    if (pdfs.length) {
      setPdfQueue(pdfs);
      setDocOpen(true);
    }
  }

  async function uploadDirect(files) {
    if (!files?.length) return;
    setBusy(true);
    setProgress({ done: 0, total: files.length });
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      const { kind, category: targetCategory, mime } = classifyUpload(f, category);
      try {
        const raw = await readFileAsDataURL(f);
        const data = kind === "image" ? await downsizeImage(raw, 1600, 0.85) : raw;
        const created = await uploadImage(projectId, {
          project_id: projectId,
          data,
          filename: f.name,
          category: targetCategory,
          mime,
        });
        onUploaded?.(created);
        setProgress({ done: i + 1, total: files.length });
        if (kind === "image") {
          analyzeImage({ image_data: data, image_id: created.id })
            .then((res) =>
              onUploaded?.({ ...created, ...res, ai_analyzed: true })
            )
            .catch(() => {});
        }
      } catch (e) {
        console.error(e);
        toast.error(`Failed: ${f.name}`);
      }
    }
    setBusy(false);
    setProgress({ done: 0, total: 0 });
    toast.success(`${files.length} file${files.length === 1 ? "" : "s"} added`);
  }

  async function importUrls(urls) {
    if (!urls?.length) return;
    setBusy(true);
    setProgress({ done: 0, total: urls.length });
    for (let i = 0; i < urls.length; i++) {
      const u = urls[i];
      try {
        const created = await importFromUrl({
          project_id: projectId,
          url: u,
          category,
        });
        onUploaded?.(created);
        setProgress({ done: i + 1, total: urls.length });
        if (created?.mime?.startsWith("image/")) {
          analyzeImage({ image_data: created.data, image_id: created.id })
            .then((res) =>
              onUploaded?.({ ...created, ...res, ai_analyzed: true })
            )
            .catch(() => {});
        }
      } catch (e) {
        console.error(e);
        toast.error(`URL import failed: ${u.slice(0, 60)}`);
      }
    }
    setBusy(false);
    setProgress({ done: 0, total: 0 });
  }

  function onDragEnter(e) {
    if (!hasUsableData(e)) return;
    e.preventDefault();
    counter.current += 1;
    setOver(true);
  }
  function onDragOver(e) {
    if (!hasUsableData(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }
  function onDragLeave() {
    counter.current = Math.max(0, counter.current - 1);
    if (counter.current === 0) setOver(false);
  }
  async function onDrop(e) {
    e.preventDefault();
    counter.current = 0;
    setOver(false);
    const dt = e.dataTransfer;
    if (!dt) return;
    const files = Array.from(dt.files || []);
    if (files.length > 0) {
      await uploadFiles(files);
      return;
    }
    // No files — try URL drops (drag from another website)
    const urls = extractUrls(dt);
    if (urls.length > 0) {
      await importUrls(urls);
    }
  }

  return (
    <div
      className={`relative ${className}`}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-testid="global-dropzone"
    >
      {children}
      <DocumentImportDialog
        open={docOpen}
        onOpenChange={setDocOpen}
        files={pdfQueue}
        projectId={projectId}
        category={category}
        onProcessed={(item) => onUploaded?.(item)}
      />
      {(over || busy) && (
        <div
          className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-stone-900/60 backdrop-blur-sm"
          data-testid="dropzone-overlay"
        >
          <div className="bg-white rounded-3xl shadow-2xl px-10 py-8 flex flex-col items-center gap-3 min-w-[320px]">
            {busy ? (
              <>
                <Loader2 className="w-8 h-8 text-stone-900 animate-spin" />
                <p className="font-display text-2xl text-stone-900">
                  Uploading {progress.done}/{progress.total}
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center gap-4 text-stone-900">
                  <ImageIcon className="w-7 h-7" strokeWidth={1.5} />
                  <Upload className="w-9 h-9" strokeWidth={1.5} />
                  <FileText className="w-7 h-7" strokeWidth={1.5} />
                </div>
                <p className="font-display text-2xl text-stone-900">
                  Drop to add to project
                </p>
                <p className="text-stone-500 text-sm">
                  Images & PDFs · URLs from other sites
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function hasUsableData(e) {
  const t = e.dataTransfer?.types;
  if (!t) return false;
  // chrome may give DataTransferItemList types only on drop, but check eagerly
  return (
    t.includes("Files") ||
    t.includes("text/uri-list") ||
    t.includes("text/plain") ||
    t.includes("text/html")
  );
}

function extractUrls(dt) {
  const urls = new Set();
  const uri = dt.getData("text/uri-list");
  if (uri) {
    uri
      .split(/\r?\n/)
      .filter((l) => l && !l.startsWith("#"))
      .forEach((l) => urls.add(l.trim()));
  }
  const plain = dt.getData("text/plain") || "";
  plain
    .split(/\s+/)
    .filter((s) => /^https?:\/\//i.test(s))
    .forEach((s) => urls.add(s));
  // Try to extract img src from HTML drops (drag-from-page)
  const html = dt.getData("text/html") || "";
  const re = /<img[^>]+src=["']([^"']+)["']/gi;
  let m;
  while ((m = re.exec(html))) {
    const src = m[1];
    if (src.startsWith("http")) urls.add(src);
  }
  return Array.from(urls);
}

// ---------- URL Import Dialog (paste a URL) ----------
export function UrlImportDialog({ projectId, category = "inspiration", onUploaded, trigger }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!url.trim()) return;
    setBusy(true);
    try {
      const created = await importFromUrl({
        project_id: projectId,
        url: url.trim(),
        category,
      });
      onUploaded?.(created);
      toast.success("Imported from URL");
      if (created?.mime?.startsWith("image/")) {
        analyzeImage({ image_data: created.data, image_id: created.id })
          .then((res) => onUploaded?.({ ...created, ...res, ai_analyzed: true }))
          .catch(() => {});
      }
      setUrl("");
      setOpen(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to import URL";
      toast.error(typeof msg === "string" ? msg : "Failed to import URL");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            Import from URL
          </DialogTitle>
          <DialogDescription className="text-sm text-stone-500">
            Paste a direct image URL, a PDF URL, or a Pinterest pin link. We'll
            fetch and save it to your library.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="url-import-form">
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://pinterest.com/pin/... or .jpg/.pdf URL"
            data-testid="url-import-input"
            autoFocus
            required
          />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              data-testid="url-import-submit"
              className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Link2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
              )}
              Import
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
